import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWorkspaceLayout } from "@/context/LayoutContext";
import { useChat } from "@/context/ChatContext";
import api from "@/lib/api";
import { LOGO_DARK, LOGO_LIGHT } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calculator, History, Boxes, Layers, Percent, BarChart3,
  ShieldCheck, Sun, Moon, LogOut, Menu, X, Plug, Users as UsersIcon,
  LayoutGrid, Check, Truck, Building2, MessageSquare, KeyRound, Radar, Zap, Warehouse,
  CalendarDays, Lightbulb, TrendingUp,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Live Map", icon: Truck, end: true },
  { to: "/workspace", label: "Quote Workspace", icon: Calculator },
  { to: "/book-now", label: "Book Now", icon: Zap },
  { to: "/history", label: "Quote History", icon: History },
  { to: "/shipments", label: "Shipments", icon: Radar },
  { to: "/customers", label: "Customers", icon: Building2 },
  { to: "/messages", label: "Messages", icon: MessageSquare },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/analytics", label: "Insights", icon: BarChart3 },
  { to: "/requests", label: "Dev Requests", icon: Lightbulb },
  { type: "divider", label: "Admin", admin: true },
  { to: "/products", label: "Product Database", icon: Boxes, admin: true },
  { to: "/freight-classes", label: "Freight Classes", icon: Layers, admin: true },
  { to: "/fak-rules", label: "FedEx FAK Rules", icon: Percent, admin: true },
  { to: "/rules", label: "Pricing Rules", icon: Percent, admin: true },
  { to: "/upcharge-rules", label: "Upcharge Rules", icon: TrendingUp, admin: true },
  { to: "/validation", label: "Rate Validation", icon: ShieldCheck, admin: true },
  { to: "/integrations", label: "Integrations", icon: Plug, admin: true },
  { to: "/ship-origins", label: "Ship-From Locations", icon: Warehouse, admin: true },
  { to: "/developer", label: "API Access", icon: KeyRound, admin: true },
  { to: "/users", label: "User Management", icon: UsersIcon, admin: true },
];

function ThemeToggle() {
  const [dark, setDark] = useState(document.documentElement.classList.contains("dark"));
  const toggle = () => {
    const el = document.documentElement;
    if (dark) {
      el.classList.remove("dark");
      localStorage.setItem("phs_theme", "light");
    } else {
      el.classList.add("dark");
      localStorage.setItem("phs_theme", "dark");
    }
    setDark(!dark);
  };
  return (
    <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" className="h-9 w-9">
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}

function BrandLogo({ className = "h-9" }) {
  return (
    <>
      <img src={LOGO_DARK} alt="PHS Freight" className={`${className} w-auto object-contain block dark:hidden`} data-testid="brand-logo-light-theme" />
      <img src={LOGO_LIGHT} alt="PHS Freight" className={`${className} w-auto object-contain hidden dark:block brightness-0 invert`} data-testid="brand-logo-dark-theme" />
    </>
  );
}

function LayoutSelector() {
  const { layout, setLayout, layouts } = useWorkspaceLayout();
  const current = layouts.find((l) => l.id === layout) || layouts[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 gap-1.5 px-2.5" data-testid="layout-selector">
          <LayoutGrid className="h-4 w-4" />
          <span className="hidden text-xs font-semibold sm:inline">{current.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="label-caps">Workspace Layout</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {layouts.map((l) => (
          <DropdownMenuItem
            key={l.id}
            onClick={() => setLayout(l.id)}
            className="flex items-start gap-2"
            data-testid={`layout-option-${l.id}`}
          >
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${l.id === layout ? "text-[hsl(var(--primary))] opacity-100" : "opacity-0"}`} />
            <div>
              <div className="text-sm font-semibold">{l.name} <span className="font-normal text-muted-foreground">· {l.tagline}</span></div>
              <div className="text-xs leading-snug text-muted-foreground">{l.description}</div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CarrierModeBadge() {
  const [live, setLive] = useState(null);
  useEffect(() => {
    api.get("/carriers/status").then((r) => {
      setLive((r.data || []).filter((c) => c.code !== "MANUAL" && c.mode === "live").length);
    }).catch(() => {});
  }, []);
  if (live === null) return null;
  if (live > 0) {
    return (
      <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--success))]" data-testid="carrier-mode-badge">
        {live} Carrier{live > 1 ? "s" : ""} · Live
      </span>
    );
  }
  return (
    <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--warning))]" data-testid="carrier-mode-badge">
      Carriers · Mock Mode
    </span>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { unread } = useChat();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(0);

  useEffect(() => {
    let active = true;
    const load = () =>
      api.get("/feature-requests/count").then((r) => active && setReqOpen(r.data?.open || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-border">
        <BrandLogo className="h-9" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.filter((item) => !item.admin || user?.role === "admin").map((item, i) =>
          item.type === "divider" ? (
            <div key={i} className="label-caps px-3 pt-4 pb-1.5">{item.label}</div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.to === "/" ? "live-map" : item.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {item.label}
              {item.to === "/messages" && unread.total > 0 && (
                <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[hsl(var(--primary))] px-1.5 text-[11px] font-bold text-white" data-testid="nav-messages-badge">
                  {unread.total > 99 ? "99+" : unread.total}
                </span>
              )}
              {item.to === "/requests" && reqOpen > 0 && (
                <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[hsl(var(--primary))] px-1.5 text-[11px] font-bold text-white" data-testid="nav-requests-badge">
                  {reqOpen > 99 ? "99+" : reqOpen}
                </span>
              )}
            </NavLink>
          ),
        )}
      </nav>
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-2">
          <NavLink
            to="/profile"
            onClick={() => setOpen(false)}
            className="flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-2 transition hover:bg-secondary/60"
            data-testid="profile-link"
          >
            {user?.avatar || user?.picture ? (
              <img src={user.avatar || user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-bold uppercase">
                {(user?.name || "U").slice(0, 2)}
              </div>
            )}
            <div className="min-w-0 flex-1 text-left">
              <div className="truncate text-sm font-semibold">{user?.name}</div>
              <div className="label-caps">{user?.role}</div>
            </div>
          </NavLink>
          <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button" className="h-8 w-8 shrink-0">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 border-r border-border bg-card lg:block">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r border-border bg-card">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-border bg-card/80 px-4 backdrop-blur">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden h-9 w-9" onClick={() => setOpen(!open)}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="font-heading text-sm font-bold tracking-tight text-muted-foreground">
              Premier Handling Solutions
            </span>
          </div>
          <div className="flex items-center gap-1">
            <CarrierModeBadge />
            <LayoutSelector />
            <ThemeToggle />
          </div>
        </header>
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
